import Vue from "vue";
import * as Sentry from "@sentry/vue";
import { env } from "@/vars";

// Client-side error monitoring (Sentry). See vendictnew/docs/client-error-monitoring-benchmark.md.
// @sentry/vue v7 — last line that supports Vue 2 (v8+ dropped it). Replay needs >= 7.27.
export default function () {
  const dsn = process.env.SENTRY_DSN;

  // Only run on deployed environments that provide a DSN.
  // Skip on localhost only (the deployed "dev" env shares env === "dev",
  // so gating on env would silence it too). Keeps local builds quiet
  // without burning quota, while dev/qa/prod deployments report.
  const isLocalhost =
    typeof window !== "undefined" &&
    /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
  if (!dsn || isLocalhost) return;

  Sentry.init({
    Vue,
    dsn,
    environment: env,
    release: process.env.SENTRY_RELEASE || undefined,

    integrations: [
      new Sentry.Replay({
        maskAllText: true, // no PII in replays
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],

    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0, // don't film normal sessions (save quota)
    replaysOnErrorSampleRate: 1.0, // film only the sessions that error

    // --- Noise filtering (free of quota: dropped SDK-side, never sent) ---
    ignoreErrors: [
      "ResizeObserver loop limit exceeded", // classic Vue/element-ui noise
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      /AbortError/, // cancelled requests
      // Firebase abuse protection blocking a device (auth/too-many-requests):
      // fired by bots/headless crawlers hammering signInAnonymously, not actionable.
      // Matched by message too because the rejection may surface without a .code.
      // Two forms: firebase v8 spells out the prose, v9 compresses the message
      // to "Firebase: Error (auth/too-many-requests)." — cover both so the
      // pending v8→v9 migration doesn't silently re-open the noise.
      /blocked all requests from this device/i,
      /auth\/too-many-requests/,
      // vue-router benign error when navigating to the current route
      // (visitor re-clicks the active nav link) — see TRUSTPAGE-4.
      /NavigationDuplicated/,
      "Avoided redundant navigation to current location",
      // Raw network failures reaching Sentry unhandled — visitor
      // connectivity, not actionable. Covers the firebase-auth v8 iframe
      // ping timeout, which rejects a bare Error("Network Error") with no
      // .code so the beforeSend code check below can't catch it (see
      // TRUSTPAGE-D/E), and axios "Request aborted" when the visitor
      // navigates away mid-request (see TRUSTPAGE-6). Tradeoff: also mutes
      // axios "Network Error" events; connectivity BUGS still surface
      // through their consequences (cf. TRUSTPAGE-9 logout-on-network-error).
      "Network Error",
      "Request aborted",
    ],
    denyUrls: [
      /chrome-extension:\/\//i, // browser extensions
      /moz-extension:\/\//i,
      /googletagmanager\.com/i, // GTM
      /google-analytics\.com/i,
      // Third-party performance beacon injected on customer domains
      // (Cloudflare web-vitals); crashes on legacy browsers/bots — see TRUSTPAGE-G/F/H/J.
      /beacon\.min\.js/i,
    ],
    beforeSend(event, hint) {
      const err = hint && hint.originalException;
      // Firebase offline noise — not actionable.
      if (err && err.code === "auth/network-request-failed") return null;
      // Firebase abuse protection (device blocked after too many auth
      // requests) — bot/crawler traffic, see TRUSTPAGE-C.
      if (err && err.code === "auth/too-many-requests") return null;
      return event; // return null = drop the event
    },
  });
}
